import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Chip,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCastingStore from '@modules/casting/store/casting.store.ts';
import { ReloadIcon, MoreIcon } from '@components/icons/svg-icon.tsx';
import { RoutePaths } from '@utils/route.utils.ts';
import { isElectronApp } from '@shared/hooks/use-is-electron.ts';
import AppImage from '@components/app-image/app-image.tsx';
import isolationIcon from '@assets/icons/isolation-mode.svg';
import serverSetupIcon from '@assets/icons/server-setup.svg';
import {
  PhoneIcon,
  TvIcon,
  LinkIcon,
  WarningIcon,
  HistoryIcon,
  CheckCircleIcon,
  CancelIcon,
  WifiIcon,
  WifiOffIcon,
  SearchIcon
} from '@modules/casting/components/casting-icons/casting-icons.tsx';
import MetricCard from '@modules/casting/components/metric-card/metric-card.tsx';
import SectionCard from '@modules/casting/components/section-card/section-card.tsx';
import SearchableTable from '@modules/casting/components/searchable-table/searchable-table.tsx';
import AssignChromecastDialog from '@modules/casting/components/assign-chromecast-dialog/assign-chromecast-dialog.tsx';
import {
  formatDate,
  formatTimeAgo,
  formatIpAddress,
  isDeviceOnline,
  calculateConnectionRate
} from '@modules/casting/utils/casting.utils.ts';
import { ChromecastDevice } from '@api/casting.api.ts';
import './casting-page.scss';

const CastingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data,
    castGatewayUrl,
    lastRefresh,
    isLoading,
    getCastData,
    refreshCastData,
    assignChromecast,
    unassignChromecast,
    assignmentLoading
  } = useCastingStore();
  const [needsDesktopApp, setNeedsDesktopApp] = useState<boolean | null>(null);
  const [needsServerSetup, setNeedsServerSetup] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedChromecast, setSelectedChromecast] =
    useState<ChromecastDevice | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<{
    element: HTMLElement | null;
    device: ChromecastDevice | null;
    type: 'assign' | 'unassign';
  }>({ element: null, device: null, type: 'assign' });

  useEffect(() => {
    const checkAndLoad = async () => {
      const isElectron = isElectronApp();

      try {
        const { getInternalServers } =
          await import('@api/internal-servers-configuration.api.ts');
        const { InternalServerEnum } =
          await import('@models/internal-servers-configuration.model.ts');

        const internalServers = await getInternalServers();
        const castGateway = internalServers.find(
          (server) => server.name === InternalServerEnum.CastGateway
        );

        if (!isElectron) {
          if (!castGateway) {
            setNeedsDesktopApp(true);
            return;
          }
        } else {
          if (!castGateway) {
            setNeedsServerSetup(true);
            return;
          }
        }

        setNeedsDesktopApp(false);
        setNeedsServerSetup(false);
        getCastData();
      } catch (error) {
        if (!isElectron) {
          setNeedsDesktopApp(true);
        } else {
          setNeedsServerSetup(true);
        }
      }
    };

    checkAndLoad();

    const interval = setInterval(() => {
      if (needsDesktopApp === false && !needsServerSetup) {
        refreshCastData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [getCastData, refreshCastData, needsDesktopApp, needsServerSetup]);

  // Split chromecasts into assigned and unassigned
  const { assignedChromecasts, unassignedChromecasts } = useMemo(() => {
    if (!data?.allChromecasts) {
      return { assignedChromecasts: [], unassignedChromecasts: [] };
    }

    const assigned = data.allChromecasts.filter((device) => device.isAssigned);
    const unassigned = data.allChromecasts.filter(
      (device) => !device.isAssigned
    );

    return {
      assignedChromecasts: assigned,
      unassignedChromecasts: unassigned
    };
  }, [data?.allChromecasts]);

  const connectionRate = data
    ? calculateConnectionRate(data.totalConnections, data.totalPhones)
    : 0;

  const disconnectedPhones = data
    ? data.phoneToRoomConnections.filter((c) => !c.isConnected).length
    : 0;

  const handleOpenAssignDialog = (chromecast: ChromecastDevice) => {
    setSelectedChromecast(chromecast);
    setAssignDialogOpen(true);
    handleCloseActionMenu();
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedChromecast(null);
  };

  const handleAssign = async (
    chromecastUuid: string,
    roomNumber: string,
    chromecastName?: string,
    chromecastModel?: string,
    lastKnownIp?: string
  ) => {
    return await assignChromecast({
      chromecastUuid,
      roomNumber,
      chromecastName,
      chromecastModel,
      lastKnownIp
    });
  };

  const handleUnassign = async (chromecastUuid: string) => {
    await unassignChromecast(chromecastUuid);
    handleCloseActionMenu();
  };

  const handleOpenActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    device: ChromecastDevice,
    type: 'assign' | 'unassign'
  ) => {
    setActionMenuAnchor({ element: event.currentTarget, device, type });
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor({ element: null, device: null, type: 'assign' });
  };

  // Loading while checking
  if (needsDesktopApp === null && !needsServerSetup) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Server setup message
  if (needsServerSetup) {
    return (
      <div className="casting-dashboard w-full h-full flex items-start justify-center pt-20">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={3}
          className="w-full max-w-md"
        >
          <AppImage
            variant="image"
            previewSrc={serverSetupIcon}
            objectFit={'contain'}
            alt="Server Setup"
            fallback="N/A"
            disableModalClick
          />
          <Typography
            variant="h5"
            className="!font-semibold !text-[var(--dark-2)] !text-center"
          >
            {t('casting_server_setup_required_title', 'Server Setup Required')}
          </Typography>
          <Typography
            variant="body1"
            className="!text-center !opacity-70 max-lg-xl:text-[14px]!"
          >
            {t(
              'casting_server_setup_required_message',
              'Your Cast Gateway Server is not configured yet. Please open the Settings page, add the correct server details, and then return to try again.'
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              navigate(
                RoutePaths.Core.InternalServersConfiguration.absolutePath
              )
            }
            sx={{ mt: 2 }}
          >
            {t('server_setup', 'Server Setup')}
          </Button>
        </Box>
      </div>
    );
  }

  // Desktop app download message
  if (needsDesktopApp === true) {
    return (
      <div className="casting-dashboard w-full h-full flex items-start justify-center pt-20">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={3}
          className="w-full max-w-md"
        >
          <AppImage
            variant="image"
            previewSrc={isolationIcon}
            objectFit={'contain'}
            alt="Isolation"
            fallback="N/A"
            disableModalClick
          />
          <Typography
            variant="h5"
            className="!font-semibold !text-[var(--dark-2)] !text-center"
          >
            {t('casting_desktop_app_required_title', 'Desktop App Required')}
          </Typography>
          <Typography
            variant="body1"
            className="!text-center !opacity-70 max-lg-xl:text-[14px]!"
          >
            {t(
              'casting_desktop_app_required_message',
              'The Casting service requires the desktop app to access internal servers. Please download and install the desktop app to use this feature.'
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(RoutePaths.Core.DesktopApp.absolutePath)}
            sx={{ mt: 2 }}
          >
            {t('download_desktop_app', 'Download Desktop App')}
          </Button>
        </Box>
      </div>
    );
  }

  if (isLoading() && !data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Authorized Phones columns
  const authorizedPhonesColumns = [
    {
      id: 'roomNumber',
      label: t('room', 'Room'),
      field: 'roomNumber',
      render: (value: string) => (
        <Chip label={value} size="small" color="primary" variant="outlined" />
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'deviceName',
      label: t('device_name', 'Device Name'),
      field: 'deviceName',
      render: (value: string) => <span>{value}</span>,
      showSearch: true,
      showSort: true
    },
    {
      id: 'clientIp',
      label: t('ip_address', 'IP Address'),
      field: 'clientIp',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {formatIpAddress(value)}
        </code>
      ),
      showSearch: true,
      showSort: true,
      searchValue: (value: string) => {
        const formatted = formatIpAddress(value);
        return `${value} ${formatted}`;
      }
    },
    {
      id: 'port',
      label: t('port', 'Port'),
      field: 'port',
      render: (value: number) => <span>{value}</span>,
      showSearch: false,
      showSort: true
    }
  ];

  // Unassigned Chromecast Devices columns
  const unassignedDevicesColumns = [
    {
      id: 'id',
      label: t('chromecast_id', 'Chromecast ID'),
      field: 'id',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{value}</code>
      ),
      showSearch: true,
      showSort: true,
      width: 250
    },
    {
      id: 'ip',
      label: t('ip_address', 'IP Address'),
      field: 'ip',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {formatIpAddress(value)}
        </code>
      ),
      showSearch: true,
      showSort: true,
      width: 150
    },
    {
      id: 'model',
      label: t('model', 'Model'),
      field: 'model',
      render: (value: string) => (
        <Typography variant="body2" className="!opacity-70">
          {value || 'Unknown'}
        </Typography>
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'status',
      label: t('status', 'Status'),
      field: 'lastSeen',
      render: (value: string) => {
        const online = isDeviceOnline(value);
        return (
          <Chip
            icon={online ? <WifiIcon /> : <WifiOffIcon />}
            label={online ? 'Online' : 'Offline'}
            size="small"
            color={online ? 'success' : 'default'}
          />
        );
      },
      showSearch: false,
      showSort: true
    },
    {
      id: 'actions',
      label: t('actions', 'Actions'),
      field: 'id',
      width: 80,
      render: (_value: string, row: ChromecastDevice) => (
        <IconButton
          className="!p-0 !h-[20px] !w-[20px]"
          disableRipple
          onClick={(e) => handleOpenActionMenu(e, row, 'assign')}
          disabled={assignmentLoading}
        >
          <MoreIcon />
        </IconButton>
      ),
      showSearch: false,
      showSort: false
    }
  ];

  // Assigned Chromecast Devices columns
  const assignedDevicesColumns = [
    {
      id: 'assignedRoom',
      label: t('room', 'Room'),
      field: 'assignedRoom',
      render: (value: string | null) =>
        value ? (
          <Chip label={value} size="small" color="primary" variant="outlined" />
        ) : (
          <span className="!opacity-50">N/A</span>
        ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'id',
      label: t('chromecast_id', 'Chromecast ID'),
      field: 'id',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">{value}</code>
      ),
      showSearch: true,
      showSort: true,
      width: 250
    },
    {
      id: 'ip',
      label: t('ip_address', 'IP Address'),
      field: 'ip',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {formatIpAddress(value)}
        </code>
      ),
      showSearch: true,
      showSort: true,
      width: 150
    },
    {
      id: 'model',
      label: t('model', 'Model'),
      field: 'model',
      render: (value: string) => (
        <Typography variant="body2" className="!opacity-70">
          {value || 'Unknown'}
        </Typography>
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'status',
      label: t('status', 'Status'),
      field: 'lastSeen',
      render: (value: string) => {
        const online = isDeviceOnline(value);
        return (
          <Chip
            icon={online ? <WifiIcon /> : <WifiOffIcon />}
            label={online ? 'Online' : 'Offline'}
            size="small"
            color={online ? 'success' : 'default'}
          />
        );
      },
      showSearch: false,
      showSort: true
    },
    {
      id: 'actions',
      label: t('actions', 'Actions'),
      field: 'id',
      width: 80,
      render: (_value: string, row: ChromecastDevice) => (
        <IconButton
          className="!p-0 !h-[20px] !w-[20px]"
          disableRipple
          onClick={(e) => handleOpenActionMenu(e, row, 'unassign')}
          disabled={assignmentLoading}
        >
          <MoreIcon />
        </IconButton>
      ),
      showSearch: false,
      showSort: false
    }
  ];

  // Phone to Room Connections columns
  const phoneToRoomConnectionsColumns = [
    {
      id: 'roomNumber',
      label: t('room_number', 'Room Number'),
      field: 'roomNumber',
      render: (value: string) => (
        <Chip label={value} size="small" color="primary" variant="outlined" />
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'phoneIp',
      label: t('phone_ip', 'Phone IP'),
      field: 'phoneIp',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {formatIpAddress(value)}
        </code>
      ),
      showSearch: true,
      showSort: true,
      searchValue: (value: string) => {
        const formatted = formatIpAddress(value);
        return `${value} ${formatted}`;
      }
    },
    {
      id: 'chromecastName',
      label: t('chromecast', 'Chromecast'),
      field: 'chromecastName',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <TvIcon fontSize="small" className="!text-gray-400" />
          <span>{value}</span>
        </div>
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'chromecastIp',
      label: t('chromecast_ip', 'Chromecast IP'),
      field: 'chromecastIp',
      render: (value: string) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {formatIpAddress(value)}
        </code>
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'proxyPort',
      label: t('proxy_port', 'Proxy Port'),
      field: 'proxyPort',
      render: (value: number) => (
        <Chip label={`Port ${value}`} size="small" variant="outlined" />
      ),
      showSearch: false,
      showSort: true
    },
    {
      id: 'isConnected',
      label: t('connection_status', 'Connection Status'),
      field: 'isConnected',
      render: (value: boolean) => (
        <Chip
          icon={value ? <CheckCircleIcon /> : <CancelIcon />}
          label={value ? 'Connected' : 'Disconnected'}
          color={value ? 'success' : 'error'}
          size="small"
        />
      ),
      showSearch: false,
      showSort: true
    }
  ];

  // Audit Logs columns
  const auditLogsColumns = [
    {
      id: 'timestamp',
      label: t('timestamp', 'Timestamp'),
      field: 'timestamp',
      render: (value: string) => (
        <Typography variant="body2">{formatDate(value)}</Typography>
      ),
      showSearch: false,
      showSort: true
    },
    {
      id: 'type',
      label: t('type', 'Type'),
      field: 'type',
      render: (value: string) => (
        <Chip
          icon={value === 'query' ? <SearchIcon /> : <HistoryIcon />}
          label={value === 'query' ? 'Query' : 'Response'}
          color={value === 'query' ? 'primary' : 'secondary'}
          size="small"
        />
      ),
      showSearch: true,
      showSort: true
    },
    {
      id: 'details',
      label: t('details', 'Details'),
      field: 'type',
      render: (value: string, row: any) => {
        if (value === 'query') {
          return (
            <div className="flex flex-col gap-1">
              <Typography variant="body2" className="!font-medium">
                Client:{' '}
                <code className="text-xs">
                  {formatIpAddress(row.clientIp || '')}
                </code>
              </Typography>
              <Typography variant="body2" className="!opacity-70">
                Query: {row.queryName} ({row.queryType})
              </Typography>
            </div>
          );
        } else {
          return (
            <div className="flex flex-col gap-1">
              <Typography variant="body2" className="!font-medium">
                Status:{' '}
                <Chip
                  label={row.status || 'N/A'}
                  size="small"
                  className="!ml-1"
                />
              </Typography>
              <Typography variant="body2" className="!opacity-70">
                {row.message}
              </Typography>
              {row.deviceName && (
                <Typography variant="body2" className="!opacity-60">
                  Device: {row.deviceName}
                </Typography>
              )}
            </div>
          );
        }
      },
      showSearch: true,
      showSort: false
    }
  ];

  return (
    <div className="casting-dashboard w-full h-full pt-2 pb-6 px-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Typography
            variant="body2"
            className="!text-[var(--dark-2)] !opacity-60"
          >
            Server: {castGatewayUrl}
          </Typography>
          <Typography
            variant="body2"
            className="!text-[var(--dark-2)] !opacity-60"
          >
            • Last updated: {formatTimeAgo(lastRefresh)}
          </Typography>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => refreshCastData()}
            disabled={isLoading()}
            className="refresh-button"
          >
            <ReloadIcon className={isLoading() ? 'animate-spin' : ''} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Metrics Cards */}
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(220px,1fr))] mb-4">
        <MetricCard
          title={t('total_phones', 'Total Phones')}
          value={data?.totalPhones || 0}
          icon={<PhoneIcon />}
          gradient="linear-gradient(62deg, #9DC4EC 15.6%, #91B9E7 79.47%)"
          subtitle={`${data?.totalPhones || 0} authorized devices`}
        />
        <MetricCard
          title={t('chromecast_devices', 'Chromecast Devices')}
          value={data?.totalChromecasts || 0}
          icon={<TvIcon />}
          gradient="linear-gradient(62deg, #C7BDF5 15.6%, #BFB1FE 79.47%)"
          subtitle={`${assignedChromecasts.length} assigned, ${unassignedChromecasts.length} unassigned`}
        />
        <MetricCard
          title={t('active_connections', 'Active Connections')}
          value={data?.totalConnections || 0}
          icon={<LinkIcon />}
          gradient="linear-gradient(62deg, #02A38E 15.6%, #018B7A 79.47%)"
          subtitle={`${connectionRate}% connection rate`}
        />
        <MetricCard
          title={t('disconnected', 'Disconnected')}
          value={disconnectedPhones}
          icon={<WarningIcon />}
          gradient="linear-gradient(62deg, #FFABAA 15.6%, #FF8281 79.47%)"
          subtitle={`${data?.totalPhones ? Math.round((disconnectedPhones / data.totalPhones) * 100) : 0}% of phones`}
        />
      </div>

      {/* Unassigned Devices */}
      {unassignedChromecasts.length > 0 && (
        <div className="mb-4">
          <SectionCard
            title={t('casting_unassigned_devices', 'Unassigned Devices')}
            icon={<TvIcon />}
          >
            <SearchableTable
              columns={unassignedDevicesColumns}
              data={unassignedChromecasts}
              searchPlaceholder={t(
                'search_unassigned_devices',
                'Search unassigned devices...'
              )}
              emptyMessage={t('no_unassigned_devices', 'No unassigned devices')}
            />
          </SectionCard>
        </div>
      )}

      {/* Assigned Devices */}
      <div className="mb-4">
        <SectionCard
          title={t('casting_assigned_devices', 'Assigned Devices')}
          icon={<TvIcon />}
        >
          <SearchableTable
            columns={assignedDevicesColumns}
            data={assignedChromecasts}
            searchPlaceholder={t(
              'search_assigned_devices',
              'Search assigned devices by room, ID, or IP...'
            )}
            emptyMessage={t(
              'no_assigned_devices',
              'No devices assigned to rooms yet'
            )}
          />
        </SectionCard>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Authorized Phones */}
        <SectionCard
          title={t('authorized_phones', 'Authorized Phones')}
          icon={<PhoneIcon />}
        >
          <SearchableTable
            columns={authorizedPhonesColumns}
            data={data?.authorizedPhones || []}
            searchPlaceholder={t(
              'search_phones',
              'Search phones by room, name, or IP...'
            )}
            emptyMessage={t('no_authorized_phones', 'No authorized phones')}
          />
        </SectionCard>

        {/* Phone to Room Connections */}
        <SectionCard
          title={t('phone_to_room_connections', 'Phone to Room Connections')}
          icon={<LinkIcon />}
        >
          <SearchableTable
            columns={phoneToRoomConnectionsColumns}
            data={data?.phoneToRoomConnections || []}
            searchPlaceholder={t(
              'search_connections',
              'Search connections by room, phone IP, or chromecast...'
            )}
            emptyMessage={t('no_connections', 'No connections found')}
          />
        </SectionCard>
      </div>

      {/* Audit Logs */}
      <div className="mb-4">
        <SectionCard
          title={t('recent_audit_logs', 'Recent Audit Logs')}
          icon={<HistoryIcon />}
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <SearchableTable
              columns={auditLogsColumns}
              data={data?.auditLogs.slice(0, 50) || []}
              searchPlaceholder={t(
                'search_logs',
                'Search logs by type, client IP, or message...'
              )}
              emptyMessage={t('no_audit_logs', 'No audit logs')}
            />
          </div>
        </SectionCard>
      </div>

      {/* Action Menu for Chromecast devices */}
      <Menu
        anchorEl={actionMenuAnchor.element}
        open={Boolean(actionMenuAnchor.element)}
        onClose={handleCloseActionMenu}
        sx={{ minWidth: 200 }}
      >
        {actionMenuAnchor.type === 'assign' ? (
          <MenuItem
            onClick={() => {
              if (actionMenuAnchor.device) {
                handleOpenAssignDialog(actionMenuAnchor.device);
              }
            }}
          >
            <ListItemIcon
              sx={{
                backgroundColor: 'var(--gray-11)',
                minWidth: '30px !important',
                height: 30,
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <LinkIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            {t('casting_assign', 'Assign')}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              if (actionMenuAnchor.device) {
                handleUnassign(actionMenuAnchor.device.id);
              }
            }}
          >
            <ListItemIcon
              sx={{
                backgroundColor: 'var(--gray-11)',
                minWidth: '30px !important',
                height: 30,
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CancelIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            {t('casting_unassign', 'Unassign')}
          </MenuItem>
        )}
      </Menu>

      {/* Assign Dialog */}
      <AssignChromecastDialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        chromecast={selectedChromecast}
        assignments={data?.assignments || []}
        onAssign={handleAssign}
        loading={assignmentLoading}
      />
    </div>
  );
};

export default CastingPage;
